import {protocolOrderErrors,type ProtocolOrder} from './protocol-order';
import {choiceHintErrors,titrationLabelingErrors,type ChoiceHintConfig,type TitrationLabelingConfig} from './titration-labeling';
import {progressModelErrors} from './reaction-progress';
import {moleculeDisplayErrors,type MoleculeReference} from './molecule-model';
import {hasMolecule} from './molecule-bank';
export type InteractiveActivityFlow = "linear" | "sections" | "free";
export type InteractiveActivityStatus = "draft" | "published";
export type InteractiveAnswerType = "mcq" | "true_false" | "numeric" | "short_text" | "open" | "formula";
export type InteractiveCorrectionMode = "automatic" | "teacher" | "hybrid";
export type ExperimentalMode = "real" | "virtual" | "provided_data";

export type InteractiveAssetRef = {
  resourceId?: string;
  resourceVersion?: number;
  id: string;
  kind: "image" | "document" | "video" | "animation" | "simulation";
  name: string;
  mimeType?: string;
  url?: string;
};

export type InteractiveBaseBlock = {
  correctionIllustrations?: import('./molecule-model').MoleculeIllustration[];
  id: string;
  visible?: boolean;
  allowAttachments?: boolean;
  /** Optional experimental variants in which this block is shown. */
  experimentalModes?: ExperimentalMode[];
  experimentalPart?: string;
  teacherGuidance?: string;
  title?: string;
  teacherNote?: string;
  studentCorrection?: string;
};

export type InteractiveTextBlock = InteractiveBaseBlock & {
  progressAnalogy?: import('./reaction-progress').ProgressModel;
  illustrations?: import('./molecule-model').MoleculeIllustration[];
  type: "text";
  content: string;
  variant?: "paragraph" | "instruction" | "method" | "warning";
};

export type InteractiveMediaBlock = InteractiveBaseBlock & {
  type: "image" | "document" | "video";
  assetId: string;
  caption?: string;
};

export type InteractiveAnimationBlock = InteractiveBaseBlock & {
  type: "animation" | "simulation";
  resourceId?: string;
  resourceVersion?: number;
  resourceConfig?: import('./resource-config').ResourceConfig;
  measurementTargetBlockId?: string;
  lockAfterCompletion?: boolean;
  assetId?: string;
  prompt?: string;
  controls?: Array<{ id: string; label: string; min?: number; max?: number; step?: number; unit?: string }>;
  expectedRelationships?: string[];
};

export type InteractiveLabInstructionBlock = InteractiveBaseBlock & {
  type: "lab_instruction";
  content: string;
  safety?: string[];
};

export type InteractiveMeasurementColumn = {
  id: string;
  label: string;
  unit?: string;
  input: "number" | "text";
  expected?: { min?: number; max?: number; tolerance?: number };
};

export type InteractiveMeasurementTableBlock = InteractiveBaseBlock & {
  dataSource?: 'manual' | 'simulation' | 'provided';
  allowMeasurementSourceChoice?: boolean;
  initialRows?: Record<string,string>[];
  readOnlyColumnIds?: string[];
  volumeSourceBlockId?: string;
  concentrationSource?: {blockId:string;solutionColumnId:string;concentrationColumnId:string};
  validateBeforeGraph?: boolean;
  calibrationSeries?: Array<{solutionId:string;solutionLabel?:string;preparationSolution?:string;concentrationMolL:number|null;samplingVolumeMl?:number;absorbance?:number;conductivity?:number}>;
  type: "measurement_table";
  resourceMeasurementTable?: boolean;
  measurementTableKind?: 'bank'|'materials'|'audio'|'dilution'|'spectro'|'ph'|'conductivity'|'colorimetric';
  acquisitionOnly?: boolean;
  prompt?: string;
  columns: InteractiveMeasurementColumn[];
  minRows?: number;
  maxRows?: number;
  competencyIds?: string[];
  correctionMode?: InteractiveCorrectionMode;
};

export type InteractiveGraphBlock = InteractiveBaseBlock & {
  graphyo?: import("./graphyo-activity").GraphyoActivityConfig;
  correctionGraphConfig?: import('./correction-graph').CorrectionGraphConfig;
  graphConfig?: Partial<import('./interactive-graph-model').GraphConfig>;
  allowRegression?: boolean;
  showRegression?: boolean;
  showEquation?: boolean;
  showR2?: boolean;
  type: "graph_task";
  prompt: string;
  sourceTableBlockId?: string;
  xColumnId?: string;
  yColumnId?: string;
  competencyIds?: string[];
  correctionMode?: InteractiveCorrectionMode;
};

export type InteractiveQuestionOption = {
  id: string;
  text: string;
  correct?: boolean;
};

export type InteractiveQuestionCorrection = {
  explanation?: string;
  acceptedAnswers?: string[];
  numericAnswer?: number;
  tolerance?: number;
  unit?: string;
  keywords?: string[];
};

export type InteractiveQuestionBlock = InteractiveBaseBlock & {
  correctionGraphConfig?: import('./correction-graph').CorrectionGraphConfig;
  correctionDiagram?: import('./titration-diagram').TitrationDiagramConfig;
  progressTable?: import('./reaction-progress').ProgressTableConfig;
  responseFields?: import('./reaction-progress').ResponseFields;
  studentCorrection?: string;
  measurementSourceBlockId?: string;
  resourceMeasurementTable?: boolean;
  measurementTableKind?: 'bank'|'materials'|'audio'|'dilution'|'spectro'|'ph'|'conductivity'|'colorimetric';
  type: "question";
  answerType: InteractiveAnswerType;
  selectionMode?: "single" | "multiple";
  answerUnit?: string;
  prompt: string;
  required?: boolean;
  hint?: string;
  progressiveHints?: string[];
  choiceHints?: ChoiceHintConfig[];
  diagramLabeling?: TitrationLabelingConfig;
  protocolOrder?: ProtocolOrder;
  /** Save and freeze this answer before revealing the hint. */
  helpSnapshotSourceBlockId?: string;
  options?: InteractiveQuestionOption[];
  correctionMode: InteractiveCorrectionMode;
  correction?: InteractiveQuestionCorrection;
  competencyIds?: string[];
  weight?: number;
  allowRevision?: boolean;
};

export type InteractiveSectionBlock = InteractiveBaseBlock & {
  type: "section";
  heading: string;
  description?: string;
};

export type InteractiveMoleculeBlock = InteractiveBaseBlock & {
  type: "molecule";
  molecule: MoleculeReference;
  interactive?: boolean;
};

export type InteractiveActivityBlock =
  | InteractiveMoleculeBlock
  | InteractiveTextBlock
  | InteractiveMediaBlock
  | InteractiveAnimationBlock
  | InteractiveLabInstructionBlock
  | InteractiveMeasurementTableBlock
  | InteractiveGraphBlock
  | InteractiveQuestionBlock
  | InteractiveSectionBlock;

export type InteractiveCompetencyRule = {
  competencyId: string;
  blockIds: string[];
  proposalMode: "success_rate" | "weighted" | "teacher_only";
  thresholds?: { A: number; B: number; C: number };
};

/** A checkpoint groups explicit evidence; question tags alone never create one. */
export type InteractiveCompetencyCheckpoint = {
  experimentalPart?: string;
  /** Optional experimental variants in which this checkpoint is shown. */
  experimentalModes?: ExperimentalMode[];
  /** Slot before blocks[index]; ties follow checkpoint array order. */
  flowPosition?: number;
  id: string;
  competencyId: string;
  evidenceBlockIds?: string[];
  positionAfterBlockId?: string;
  /** Legacy question-only checkpoints remain readable. */
  questionIds?: string[];
  positionAfterQuestionId?: string;
  evaluationMode: "manual" | "automatic_with_teacher_override";
  /** Absent = resolve criteria from the global catalogue. */
  rubric?: Record<"A" | "B" | "C" | "D", string>;
  studentDisplay: {
    showCompetenceName: boolean;
    showCriteria: boolean;
    showGradeBeforeValidation: boolean;
  };
  feedback: { enabled: boolean; automatic: boolean };
};

export type InteractiveAiImportTrace = {
  sourceType: "pdf" | "docx" | "image" | "text" | "mixed";
  sourceName?: string;
  generatedAt: string;
  decisions?: Array<{
    source?: string;
    action: "keep" | "remove" | "rewrite" | "transform" | "add";
    reason?: string;
  }>;
};

export type InteractiveActivityDefinition = {
  schemaVersion: 1;
  revision: number;
  status: InteractiveActivityStatus;
  flow: InteractiveActivityFlow;
  /** Teacher-selected source of experimental data for this activity. */
  experimentalMode?: ExperimentalMode;
  experimentalModeOverrides?: Record<string,ExperimentalMode>;
  blocks: InteractiveActivityBlock[];
  assets: InteractiveAssetRef[];
  competencyRules: InteractiveCompetencyRule[];
  competencyCheckpoints?: InteractiveCompetencyCheckpoint[];
  aiImport?: InteractiveAiImportTrace;
};

export function createBlankInteractiveActivity(): InteractiveActivityDefinition {
  return {
    schemaVersion: 1,
    revision: 1,
    status: "draft",
    flow: "linear",
    experimentalMode: "real",
    blocks: [],
    assets: [],
    competencyRules: [],
  };
}

export function interactiveCompetencyIds(value: InteractiveActivityDefinition) {
  const ids = new Set<string>();
  value.blocks.forEach((block) => {
    if ("competencyIds" in block && Array.isArray(block.competencyIds)) block.competencyIds.forEach((id) => ids.add(id));
  });
  value.competencyRules.forEach((rule) => ids.add(rule.competencyId));
  value.competencyCheckpoints?.forEach((checkpoint) => ids.add(checkpoint.competencyId));
  return [...ids];
}

export function validateInteractiveActivity(value: InteractiveActivityDefinition, allowedCompetencyIds?: string[]) {
  const errors: string[] = [];
  if (value.schemaVersion !== 1) errors.push("Version de format interactive non prise en charge.");
  if (!Number.isInteger(value.revision) || value.revision < 1) errors.push("La révision doit être un entier positif.");
  if (!["linear", "sections", "free"].includes(value.flow)) errors.push("Mode de navigation invalide.");
  if (value.experimentalMode !== undefined && !["real","virtual","provided_data"].includes(value.experimentalMode))
    errors.push("Modalité expérimentale invalide.");
  if(value.experimentalModeOverrides && Object.values(value.experimentalModeOverrides).some(m=>!["real","virtual","provided_data"].includes(m)))errors.push("Override de modalité invalide.");
  if (!Array.isArray(value.blocks)) errors.push("La liste des blocs est invalide.");

  const blockIds = new Set<string>();
  for (const block of value.blocks ?? []) {
    if(block.type==='text'&&block.progressAnalogy)errors.push(...progressModelErrors(block.progressAnalogy).map(e=>`Bloc ${block.id} : ${e}`));
    if(block.type==='question'&&block.progressTable){
      errors.push(...progressModelErrors({...block.progressTable,species:block.progressTable.species.map(s=>({...s,initial:s.initial??0,coefficient:s.coefficient??1}))}).map(e=>`Bloc ${block.id} : ${e}`));
      const tableSpecies=block.progressTable.species;
      const keys=new Set(['initial','current','final'].flatMap(row=>['x',...tableSpecies.map(s=>s.id)].map(id=>row+'.'+id)));
      if(!block.progressTable.fixedCells||Object.entries(block.progressTable.fixedCells).some(([k,v])=>!keys.has(k)||typeof v!=='string'))errors.push(`Bloc ${block.id} : cellules imposées invalides.`);
    }
    if(block.type==='question'&&(block.progressTable||block.responseFields)&&block.correctionMode!=='teacher')errors.push(`Bloc ${block.id} : les réponses structurées nécessitent une validation professeur.`);
    if(block.type==='question'&&block.responseFields&&new Set(block.responseFields.fields.map(f=>f.id)).size!==block.responseFields.fields.length)errors.push(`Bloc ${block.id} : champs dupliqués.`);
    if(block.type==="molecule" && (!block.molecule || !hasMolecule(block.molecule.moleculeId) || moleculeDisplayErrors(block.molecule).length || block.interactive!==undefined&&typeof block.interactive!=="boolean")) errors.push(`Bloc ${block.id} : molécule ou représentation invalide.`);
    for(const illustrations of [block.correctionIllustrations,block.type==='text'?block.illustrations:undefined]){
      if(illustrations!==undefined&&(!Array.isArray(illustrations)||illustrations.length>6||illustrations.some(item=>!item||!item.molecule||!hasMolecule(item.molecule.moleculeId)||moleculeDisplayErrors(item.molecule).length||item.caption!==undefined&&(typeof item.caption!=='string'||item.caption.length>2000))))errors.push(`Bloc ${block.id} : illustration moléculaire invalide.`);
    }
    if(block.allowAttachments!==undefined&&typeof block.allowAttachments!=='boolean')errors.push(`Bloc ${block.id} : autorisation des pièces jointes invalide.`);
    if (block.experimentalModes !== undefined &&
        (!Array.isArray(block.experimentalModes) || !block.experimentalModes.length ||
         new Set(block.experimentalModes).size !== block.experimentalModes.length ||
         block.experimentalModes.some(mode => !["real","virtual","provided_data"].includes(mode))))
      errors.push(`Bloc ${block.id || "sans identifiant"} : modalités expérimentales invalides.`);
    if (!block.id?.trim()) errors.push("Chaque bloc doit avoir un identifiant.");
    else if (blockIds.has(block.id)) errors.push(`Identifiant de bloc dupliqué : ${block.id}`);
    else blockIds.add(block.id);

    if (block.type === "question") {
      if (block.diagramLabeling) {
        errors.push(...titrationLabelingErrors(block.diagramLabeling).map(error => `Question ${block.id} : ${error}`));
        if (block.answerType !== 'open' || block.correctionMode !== 'teacher' || block.progressTable || block.responseFields || block.protocolOrder || block.resourceMeasurementTable) errors.push(`Question ${block.id} : la légende du titrage nécessite une réponse ouverte dédiée et une correction professeur.`);
      }
      if (block.choiceHints) {
        if (!Array.isArray(block.choiceHints) || !block.choiceHints.length || block.choiceHints.length !== block.progressiveHints?.length) errors.push(`Question ${block.id} : chaque aide à choix doit correspondre à une aide progressive.`);
        else for (const hint of block.choiceHints) errors.push(...choiceHintErrors(hint).map(error => `Question ${block.id} : ${error}`));
      }
      if(block.protocolOrder){
        errors.push(...protocolOrderErrors(block.protocolOrder).map(e=>'Question '+block.id+' : '+e));
        if(block.answerType!=='open'||!block.progressiveHints?.length)errors.push('Le jeu de protocole nécessite une réponse ouverte et des aides progressives.');
      }
      if (!block.prompt?.trim()) errors.push(`Question ${block.id || "sans identifiant"} : énoncé manquant.`);
      if (block.answerType === "mcq") {
        if (!Array.isArray(block.options) || block.options.length < 2) errors.push(`Question ${block.id} : au moins deux propositions sont nécessaires.`);
        const correct = block.options?.filter((option) => option.correct).length ?? 0;
        if (block.correctionMode !== "teacher" && correct === 0) errors.push(`Question ${block.id} : aucune bonne réponse n’est définie.`);
      }
      if (block.answerType === "numeric" && block.correctionMode !== "teacher" && block.correction?.numericAnswer === undefined) {
        errors.push(`Question ${block.id} : valeur numérique attendue manquante.`);
      }
    }

    if (block.type === "measurement_table" && block.dataSource !== undefined && !['manual','simulation','provided'].includes(block.dataSource)) errors.push('Origine des mesures invalide.');
    if (block.type === "measurement_table" && (!Array.isArray(block.columns) || block.columns.length === 0)) {
      errors.push(`Tableau ${block.id} : au moins une colonne est nécessaire.`);
    }
  }

  const competencyIds = interactiveCompetencyIds(value);
  if (competencyIds.length > 4) errors.push("Une activité interactive ne peut évaluer que 4 compétences maximum.");
  if (allowedCompetencyIds) {
    const allowed = new Set(allowedCompetencyIds);
    competencyIds.filter((id) => !allowed.has(id)).forEach((id) => errors.push(`Compétence inconnue dans l’activité : ${id}`));
  }

  for (const rule of value.competencyRules ?? []) {
    if (!rule.competencyId) errors.push("Une règle de compétence n’a pas de compétence associée.");
    if (!Array.isArray(rule.blockIds) || rule.blockIds.length === 0) errors.push(`Compétence ${rule.competencyId} : aucun bloc associé.`);
    rule.blockIds?.filter((id) => !blockIds.has(id)).forEach((id) => errors.push(`Compétence ${rule.competencyId} : bloc inconnu ${id}.`));
  }

  return errors;
}

export function parseInteractiveActivityJson(text: string): InteractiveActivityDefinition {
  const parsed = JSON.parse(text) as InteractiveActivityDefinition;
  if (!parsed || typeof parsed !== "object") throw new Error("Le JSON de l’activité est invalide.");
  if (!Array.isArray(parsed.blocks)) throw new Error("Le JSON ne contient pas de liste de blocs.");
  return {
    schemaVersion: 1,
    revision: Number.isInteger(parsed.revision) && parsed.revision > 0 ? parsed.revision : 1,
    status: parsed.status === "published" ? "published" : "draft",
    flow: parsed.flow === "sections" || parsed.flow === "free" ? parsed.flow : "linear",
    experimentalMode: ["real","virtual","provided_data"].includes(parsed.experimentalMode as string) ? parsed.experimentalMode : undefined,
    experimentalModeOverrides: parsed.experimentalModeOverrides,
    blocks: parsed.blocks,
    assets: Array.isArray(parsed.assets) ? parsed.assets : [],
    competencyRules: Array.isArray(parsed.competencyRules) ? parsed.competencyRules : [],
    ...(parsed.competencyCheckpoints !== undefined ? { competencyCheckpoints: parsed.competencyCheckpoints } : {}),
    aiImport: parsed.aiImport,
  };
}



